import { useState } from 'react';
import LogoutButton from '../Auth/LogoutButton';
import BudgetCreationForm from './BudgetCreationForm';
import styles from './BudgetCreation.module.css';

// Calculate available start months (prior, current, next)
const getAvailableMonths = () => {
    const today = new Date();
    const months = [];

    // Prior month
    const priorMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    months.push({
        value: priorMonth.toISOString().split('T')[0],
        label: priorMonth.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
    });

    // Current month
    const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    months.push({
        value: currentMonth.toISOString().split('T')[0],
        label: currentMonth.toLocaleDateString('en-US', { year: 'numeric', month: 'long' }) + ' (Current)'
    });

    // Next month
    const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
    months.push({
        value: nextMonth.toISOString().split('T')[0],
        label: nextMonth.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
    });

    return months;
};

/**
 * BudgetCreation Component
 *
 * Modal/screen for creating a new budget
 * Shows when user has no budgets available OR when user clicks "Create New Budget"
 *
 * Props:
 * - onBudgetCreated: Callback function when budget is successfully created
 * - onCancel: Optional callback function when user cancels (only shown if provided)
 */
function BudgetCreation({ onBudgetCreated, onCancel }) {
    const [budgetName, setBudgetName] = useState('');
    const availableMonths = getAvailableMonths();
    const [startMonth, setStartMonth] = useState(availableMonths[1].value);
    const [singleBankMode, setSingleBankMode] = useState(false);
    const [error, setError] = useState('');
    const [isCreating, setIsCreating] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validation
        if (!budgetName.trim()) {
            setError('Budget name is required');
            return;
        }

        if (budgetName.length > 100) {
            setError('Budget name must be 100 characters or less');
            return;
        }

        if (!startMonth) {
            setError('Start month is required');
            return;
        }

        setIsCreating(true);

        try {
            // Call onBudgetCreated with the form data
            await onBudgetCreated({ budgetName: budgetName.trim(), startMonth, singleBankMode });
        } catch (err) {
            setError(err.message || 'Failed to create budget');
            setIsCreating(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <div className={styles.header}>
                    <h2 className={styles.title}>
                        {onCancel ? 'Create a New Budget' : 'Create Your First Budget'}
                    </h2>
                    <LogoutButton />
                </div>
                <p className={styles.description}>
                    {onCancel
                        ? 'Set up an additional budget to track separate finances.'
                        : 'Get started by setting up your budget. You\'ll be able to track expenses and manage your finances.'
                    }
                </p>

                <form onSubmit={handleSubmit}>
                    <BudgetCreationForm
                        budgetName={budgetName}
                        setBudgetName={setBudgetName}
                        startMonth={startMonth}
                        setStartMonth={setStartMonth}
                        singleBankMode={singleBankMode}
                        setSingleBankMode={setSingleBankMode}
                        availableMonths={availableMonths}
                        isCreating={isCreating}
                        error={error}
                    />

                    {/* Buttons */}
                    <div className={styles.buttonGroup}>
                        {/* Cancel Button - only show if onCancel provided */}
                        {onCancel && (
                            <button
                                type="button"
                                onClick={onCancel}
                                disabled={isCreating}
                                className={styles.cancelButton}
                            >
                                Cancel
                            </button>
                        )}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            disabled={isCreating}
                            className={styles.submitButton}
                        >
                            {isCreating ? 'Creating Budget...' : 'Create Budget'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default BudgetCreation;
